import { useRef, useState } from "react";
import { MButton } from "../components/MButton";
import MModal from "../components/MModal";
import { Box, FormHelperText, LinearProgress, LinearProgressProps, Typography } from "@mui/material";
import NotebookImage from "../images/notebook.png";
import MFileUpload from "../components/MFileUpload";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { updateStudyMeasurement } from "../api/study";
import { uploadAnonymousHolterFileV2, uploadAnonymousSmallHolterFileV2 } from "../services/dmsUpload/anonymousHolterUploadService";
import { getDmsUploadFileRole, getFileExtension, selectLargestDmsFile } from "../services/dmsUpload/dmsFiles";

export function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    props.value,
                )}%`}</Typography>
            </Box>
        </Box>
    );
}

interface UploadDMSProps {
    name?: string;
    device: any;
    patientIdUnique: string;
}

const CHUNK_SIZE = 6;
const CHUNK_SIZE_BYTES = CHUNK_SIZE * 1024 * 1024;

interface FileInterface {
    file?: File;
    fileHandle?: any;
    fileSizeBytes?: number;
    fileName?: string;
    fileType?: string;
}

interface FileCandidate {
    file: File;
    fileHandle?: any;
    name: string;
    size: number;
}

type UploadItemStatus = 'idle' | 'uploading' | 'success' | 'error' | 'skipped';
type UploadItemKey = 'recording' | 'log' | 'diary' | 'finalization';

const initialUploadStatuses: Record<UploadItemKey, UploadItemStatus> = {
    recording: 'idle',
    log: 'idle',
    diary: 'idle',
    finalization: 'idle',
};

const combineUploads = (parts: number[]): number => {
    if (!parts.length) {
        return 0;
    }

    // Ensure all parts are 100%
    if (parts.every(part => part === 100)) {
        return 100; // If all parts are 100%, the combined upload is 100%
    } else {
        // If not all parts are 100%, calculate the average percentage
        const total = parts.reduce((accumulator, part) => accumulator + part, 0);
        const combinedPercentage = total / parts.length;
        return combinedPercentage;
    }
}

const waitForNextPaint = () =>
    new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
    });

const formatFileSize = (sizeBytes?: number) => {
    if (typeof sizeBytes !== 'number') {
        return 'Unknown size';
    }

    if (sizeBytes === 0) {
        return '0 B';
    }

    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }

    const sizeKb = sizeBytes / 1024;
    if (sizeKb < 1024) {
        return `${sizeKb.toFixed(1)} KB`;
    }

    return `${(sizeKb / 1024).toFixed(2)} MB`;
};

export const UploadDMS = (props: UploadDMSProps) => {
    const { name = 'Upload a Recording', device, patientIdUnique } = props;
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('0');

    const [uploadingProgress, setUploadingProgress] = useState(0);
    const [uploadingEventsProgress, setUploadingEventsProgress] = useState(0);
    const [uploadingLogsProgress, setUploadingLogsProgress] = useState(0);
    const [uploadStatuses, setUploadStatuses] = useState<Record<UploadItemKey, UploadItemStatus>>(initialUploadStatuses);
    const [uploadError, setUploadError] = useState("");
    const isUploadInFlightRef = useRef(false);

    const [recordingFileData, setRecordingFileData] = useState<FileInterface | null>(null)
    const [logsFileData, setLogsFileData] = useState<FileInterface | null>(null)
    const [eventsFileData, setEventsFileData] = useState<FileInterface | null>(null)
    const [folderSelectionNotice, setFolderSelectionNotice] = useState("");

    const totalLoading = [uploadingProgress, logsFileData ? uploadingLogsProgress : null, eventsFileData ? uploadingEventsProgress : null]
        .filter((part): part is number => typeof part === 'number');
    const totalLoadingProgress = combineUploads(totalLoading);

    const updateUploadStatus = (key: UploadItemKey, status: UploadItemStatus) => {
        setUploadStatuses(prevState => ({
            ...prevState,
            [key]: status,
        }));
    }

    const createFileData = (file: File, fileHandle?: any): FileInterface => ({
        file,
        fileHandle,
        fileName: file.name,
        fileSizeBytes: file.size,
        fileType: getFileExtension(file.name)
    });

    const refreshFileData = async (fileData: FileInterface | null): Promise<FileInterface | null> => {
        if (!fileData?.fileHandle) {
            return fileData;
        }

        const file = await fileData.fileHandle.getFile();
        return createFileData(file, fileData.fileHandle);
    }

    const getUploadErrorMessage = (error: unknown) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        return "Upload failed. Try to upload the files again.";
    }

    const validateRequiredRecordingFile = (fileData: FileInterface | null = recordingFileData) => {
        if (!fileData?.file) {
            return "Recording file is missing. Try to select it again.";
        }

        if (fileData.file.size <= 0) {
            return "Browser reported the recording file as 0 B. Reconnect the device storage or select the .cdm file again.";
        }

        return "";
    }

    const handleRecordingFile = (file?: File, fileHandle?: any) => {
        if (!file) {
            setRecordingFileData(null);
            setEventsFileData(null);
            return;
        }

        setRecordingFileData(createFileData(file, fileHandle));
    }

    const handleLogsFile = (file?: File, fileHandle?: any) => {
        if (!file || file.size <= 0) {
            setLogsFileData(null);
            return;
        }

        setLogsFileData(createFileData(file, fileHandle));
    }

    const handleEventsDiaryFile = (file?: File, fileHandle?: any) => {
        if (!file || file.size <= 0) {
            setEventsFileData(null);
            return;
        }

        setEventsFileData(createFileData(file, fileHandle));
    }

    const onClose = () => {
        isUploadInFlightRef.current = false;
        setStep('0');
        setOpen(false);
        setRecordingFileData(null);
        setLogsFileData(null);
        setEventsFileData(null);
        setUploadingProgress(0);
        setUploadingLogsProgress(0);
        setUploadingEventsProgress(0);
        setUploadStatuses(initialUploadStatuses);
        setUploadError("");
        setFolderSelectionNotice("");
    }

    const startRecordingFileUploading = async (fileData: FileInterface) => {
        const serialNumber = device.device_serial_number || device.serial_number;

        if (!serialNumber) {
            throw new Error("Device serial number is missing");
        }

        await uploadAnonymousHolterFileV2({
            serialNumber,
            file: fileData.file!,
            filename: fileData.fileName,
            origin: window.location.origin,
            chunkSizeBytes: CHUNK_SIZE_BYTES,
            onProgress: setUploadingProgress,
        });
    }

    const startSmallFileUploading = async (
        fileData: FileInterface,
        onProgress: (progress: number) => void
    ) => {
        const serialNumber = device.device_serial_number || device.serial_number;

        if (!serialNumber) {
            throw new Error("Device serial number is missing");
        }

        await uploadAnonymousSmallHolterFileV2({
            serialNumber,
            file: fileData.file!,
            filename: fileData.fileName,
            onProgress,
        });
    }

    const startLogsFileUploading = async (fileData: FileInterface) => {
        await startSmallFileUploading(fileData, setUploadingLogsProgress);
    }

    const startEventsFileUploading = async (fileData: FileInterface) => {
        await startSmallFileUploading(fileData, setUploadingEventsProgress);
    }

    const startFilesUploading = async () => {
        if (isUploadInFlightRef.current) {
            return;
        }

        isUploadInFlightRef.current = true;
        setUploadError("");
        setStep('1.2');
        setUploadingProgress(0);
        setUploadingLogsProgress(0);
        setUploadingEventsProgress(0);
        setUploadStatuses({
            recording: 'uploading',
            log: logsFileData ? 'idle' : 'skipped',
            diary: eventsFileData ? 'idle' : 'skipped',
            finalization: 'idle',
        });

        try {
            await waitForNextPaint();

            const currentRecordingFileData = await refreshFileData(recordingFileData);
            setRecordingFileData(currentRecordingFileData);

            const recordingFileError = validateRequiredRecordingFile(currentRecordingFileData);

            if (recordingFileError) {
                updateUploadStatus('recording', 'error');
                setStep('2.1.1');
                throw new Error(recordingFileError);
            }
            await startRecordingFileUploading(currentRecordingFileData!);
            updateUploadStatus('recording', 'success');

            if (logsFileData) {
                const currentLogsFileData = await refreshFileData(logsFileData);
                setLogsFileData(currentLogsFileData);

                if (!currentLogsFileData?.file || currentLogsFileData.file.size <= 0) {
                    updateUploadStatus('log', 'skipped');
                } else {
                    updateUploadStatus('log', 'uploading');
                    await startLogsFileUploading(currentLogsFileData);
                    updateUploadStatus('log', 'success');
                }
            }
            if (eventsFileData) {
                const currentEventsFileData = await refreshFileData(eventsFileData);
                setEventsFileData(currentEventsFileData);

                if (!currentEventsFileData?.file || currentEventsFileData.file.size <= 0) {
                    updateUploadStatus('diary', 'skipped');
                } else {
                    updateUploadStatus('diary', 'uploading');
                    await startEventsFileUploading(currentEventsFileData);
                    updateUploadStatus('diary', 'success');
                }
            }

            updateUploadStatus('finalization', 'uploading');
            await updateStudyMeasurement({
                id: device.id,
                date_upload: new Date(),
                file_type: currentRecordingFileData!.fileType!,
                id_unique: patientIdUnique
            })
            updateUploadStatus('finalization', 'success');

            setStep('1.3');
        } catch (e) {
            console.error(e);
            setUploadError(getUploadErrorMessage(e));
            setUploadStatuses(prevState => ({
                recording: prevState.recording === 'uploading' ? 'error' : prevState.recording,
                log: prevState.log === 'uploading' ? 'error' : prevState.log,
                diary: prevState.diary === 'uploading' ? 'error' : prevState.diary,
                finalization: prevState.finalization === 'uploading' ? 'error' : prevState.finalization,
            }));
            setStep('2.1.1');
        } finally {
            isUploadInFlightRef.current = false;
        }
    }

    const handleSelectFolder = async () => {
        try {
            // Prompt the user to select a folder
            const directoryHandle = await (window as any).showDirectoryPicker(
                {
                    startIn: 'desktop'
                }
            );

            const recordingFiles: FileCandidate[] = [];
            const logFiles: FileCandidate[] = [];
            const diaryFiles: FileCandidate[] = [];

            for await (const entry of directoryHandle.values()) {
                if (entry.kind === 'file') {
                    const fileRole = getDmsUploadFileRole(entry.name);
                    if (fileRole) {
                        const fileHandle = await entry.getFile();
                        const fileCandidate = {
                            file: fileHandle,
                            fileHandle: entry,
                            name: fileHandle.name,
                            size: fileHandle.size,
                        };

                        if (fileRole === 'recording') {
                            recordingFiles.push(fileCandidate);
                        }

                        if (fileRole === 'log') {
                            logFiles.push(fileCandidate);
                        }

                        if (fileRole === 'diary') {
                            diaryFiles.push(fileCandidate);
                        }
                    }
                }
            }

            const recordingFile = selectLargestDmsFile(recordingFiles);
            const logFile = selectLargestDmsFile(logFiles);
            const diaryFile = selectLargestDmsFile(diaryFiles);

            handleRecordingFile(recordingFile?.file, recordingFile?.fileHandle);
            handleLogsFile(logFile?.file, logFile?.fileHandle);
            handleEventsDiaryFile(diaryFile?.file, diaryFile?.fileHandle);

            if (recordingFiles.length > 1 && recordingFile) {
                setFolderSelectionNotice(
                    "Multiple .cdm files found. Using the largest one."
                );
            } else {
                setFolderSelectionNotice("");
            }

            if (recordingFile) {
                setStep('1.1');
            } else {
                setStep('2.1');
            }
        } catch (e) {
            console.log('user not selected folder:', e);
        }
    };

    const renderFileSummaryRow = (label: string, fileData: FileInterface | null, optional = false) => (
        <Box display="flex" justifyContent="space-between" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" textAlign="right">
                {fileData ? `${fileData.fileName} (${formatFileSize(fileData.fileSizeBytes)})` : optional ? 'Optional, not selected' : 'Required'}
            </Typography>
        </Box>
    )

    const getStatusLabel = (status: UploadItemStatus, progress?: number) => {
        if (status === 'uploading' && typeof progress === 'number') {
            return `Uploading ${Math.round(progress)}%`;
        }

        const labels: Record<UploadItemStatus, string> = {
            idle: 'Waiting',
            uploading: 'Uploading',
            success: 'Complete',
            error: 'Failed',
            skipped: 'Skipped',
        };

        return labels[status];
    }

    const renderUploadStatusIcon = (status: UploadItemStatus) => {
        if (status === 'success') {
            return <CheckCircleOutlineIcon color="success" sx={{ fontSize: 22 }} />;
        }

        if (status === 'error') {
            return <ErrorOutlineIcon color="error" sx={{ fontSize: 22 }} />;
        }

        return (
            <Box
                sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: status === 'uploading' ? 'primary.main' : 'divider',
                }}
            />
        );
    }

    const renderUploadStatusRow = (label: string, status: UploadItemStatus, progress?: number) => (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mt={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
                {renderUploadStatusIcon(status)}
                <Typography variant="body2">{label}</Typography>
            </Box>
            <Typography variant="body2" color={status === 'error' ? 'error' : 'text.secondary'}>
                {getStatusLabel(status, progress)}
            </Typography>
        </Box>
    )

    const firstStep = (
        <Box textAlign="center">
            <Typography variant="h3">Upload a Recording</Typography>
            <Box mt={6}>
                <Typography variant="body1">Make sure the device is plugged into the computer.</Typography>
            </Box>
            <Box mt={2}>
                <img style={{ width: '213px', height: '138px' }} src={NotebookImage} alt="Notebook" />
            </Box>
            <Box mt={2} display="flex" justifyContent="center">
                <MButton
                    variant="outlined"
                    onClick={onClose}
                >
                    Cancel
                </MButton>
                <MButton sx={{ ml: 2 }} onClick={handleSelectFolder}>Start</MButton>
            </Box>
        </Box>
    );

    const modalContent = () => {
        switch (step) {
            case '0':
                return firstStep;
            case '1.1':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">
                            Recording fetched. <br />
                            Add patient diary?
                        </Typography>
                        <Box mt={4} textAlign="left">
                            {renderFileSummaryRow('Recording file (.cdm)', recordingFileData)}
                            {renderFileSummaryRow('Log file (.log)', logsFileData, true)}
                            {renderFileSummaryRow('Events diary (.pdf)', eventsFileData, true)}
                            {folderSelectionNotice && (
                                <Box mt={2}>
                                    <FormHelperText>{folderSelectionNotice}</FormHelperText>
                                </Box>
                            )}
                        </Box>
                        <Box mt={6}>
                            <Typography variant="body1"><b>Events diary</b> {eventsFileData ? <>(optional)</> : ''}</Typography>
                            {
                                !eventsFileData ? (
                                    <Box mt={2}>
                                        <Typography variant="body1">(optional, .PDF format)</Typography>
                                    </Box>
                                ) : <></>
                            }
                            <Box mt={2} display="flex" alignItems="center" justifyContent="center">
                                <MFileUpload
                                    id="events-diary-file"
                                    inputProps={{ accept: '.pdf' }}
                                    onFileChangeHandler={f => handleEventsDiaryFile(f)}
                                />
                            </Box>
                        </Box>
                        {
                            eventsFileData && eventsFileData.fileType !== 'pdf' ? (
                                <Box mt={2}>
                                    <FormHelperText sx={{ color: '#d32f2f' }}>
                                        Wrong file format. Events diary should be on .pdf format
                                    </FormHelperText>
                                </Box>
                            ) : <></>
                        }
                        <Box mt={4} display="flex" justifyContent="center">
                            <MButton
                                variant="outlined"
                                onClick={onClose}
                            >
                                Cancel
                            </MButton>
                            <MButton
                                sx={{ ml: 2 }}
                                onClick={startFilesUploading}
                                disabled={!!(eventsFileData && eventsFileData.fileType !== 'pdf')}
                            >
                                {!eventsFileData ? 'Skip diary' : 'Confirm'}
                            </MButton>
                        </Box>
                    </Box>
                );
            case '1.2':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">Uploading ...</Typography>
                        <Box mt={5}>
                            <LinearProgressWithLabel value={totalLoadingProgress} />
                        </Box>
                        <Box mt={4} textAlign="left">
                            {renderUploadStatusRow('Recording file (.cdm)', uploadStatuses.recording, uploadingProgress)}
                            {renderUploadStatusRow('Log file (.log)', uploadStatuses.log, logsFileData ? uploadingLogsProgress : undefined)}
                            {renderUploadStatusRow('Events diary (.pdf)', uploadStatuses.diary, eventsFileData ? uploadingEventsProgress : undefined)}
                            {renderUploadStatusRow('Finalize study upload', uploadStatuses.finalization)}
                        </Box>
                        <Box mt={6}>
                            <MButton variant="outlined" onClick={onClose}>Cancel</MButton>
                        </Box>
                    </Box>
                );
            case '1.3':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">Complete</Typography>
                        <Box mt={8}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 64 }} />
                            <Typography sx={{ mt: 2 }} variant="body1">Data has been uploaded</Typography>
                        </Box>
                        <Box mt={8}>
                            <MButton onClick={() => {
                                onClose();
                                window.location.reload();
                            }}>
                                Done
                            </MButton>
                        </Box>
                    </Box>
                );
            case '2.1':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">Recording not found</Typography>
                        <Box mt={6}>Select a folder that contains the recording .cdm file, or select files manually.</Box>
                        <Box mt={8} display="flex" justifyContent="center">
                            <MButton
                                variant="outlined"
                                onClick={onClose}
                            >
                                Cancel
                            </MButton>
                            <MButton sx={{ ml: 2 }} onClick={handleSelectFolder}>Try again</MButton>
                            <MButton sx={{ ml: 2 }} onClick={() => setStep('2.2')}>Select files manually</MButton>
                        </Box>
                    </Box>
                );
            case '2.1.1':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">Something went wrong</Typography>
                        <Box mt={6}>Try to upload the files again</Box>
                        {uploadError && (
                            <Box mt={2}>
                                <FormHelperText sx={{ color: '#d32f2f', textAlign: 'center' }}>
                                    {uploadError}
                                </FormHelperText>
                            </Box>
                        )}
                        <Box mt={8} display="flex" justifyContent="center">
                            <MButton
                                variant="outlined"
                                onClick={onClose}
                            >
                                Cancel
                            </MButton>
                            <MButton sx={{ ml: 2 }} onClick={handleSelectFolder}>Try again</MButton>
                            <MButton sx={{ ml: 2 }} onClick={() => setStep('2.2')}>Select files manually</MButton>
                        </Box>
                    </Box>
                );
            case '2.2':
                return (
                    <Box textAlign="center">
                        <Typography variant="h3">Select files manually</Typography>
                        <Box mt={6} textAlign="left">
                            <Box display="flex" alignItems="center" mb={3}>
                                <Typography sx={{ width: 150 }} variant="body1">Recording file</Typography>
                                <MFileUpload
                                    id="recording-file"
                                    inputProps={{ accept: '.cdm' }}
                                    onFileChangeHandler={f => handleRecordingFile(f)}
                                />
                            </Box>
                            {
                                recordingFileData && recordingFileData.fileType !== 'cdm' ? (
                                    <Box mb={2}>
                                        <FormHelperText sx={{ color: '#d32f2f' }}>
                                            Wrong file format. Recording file should be on .cdm format
                                        </FormHelperText>
                                    </Box>
                                ) : <></>
                            }
                            {
                                recordingFileData && recordingFileData.fileType === 'cdm' && recordingFileData.file?.size === 0 ? (
                                    <Box mb={2}>
                                        <FormHelperText sx={{ color: '#d32f2f' }}>
                                            Recording file is empty. Select a valid .cdm file.
                                        </FormHelperText>
                                    </Box>
                                ) : <></>
                            }
                            <Box display="flex" alignItems="center">
                                <Typography sx={{ width: 150 }} variant="body1">Log file (optional)</Typography>
                                <MFileUpload
                                    id="log-file"
                                    inputProps={{ accept: '.log' }}
                                    onFileChangeHandler={f => handleLogsFile(f)}
                                />
                            </Box>
                            {
                                logsFileData && logsFileData.fileType !== 'log' ? (
                                    <Box mb={2}>
                                        <FormHelperText sx={{ color: '#d32f2f' }}>
                                            Wrong file format. Log file should be on .log format
                                        </FormHelperText>
                                    </Box>
                                ) : <></>
                            }
                        </Box>
                        <Box mt={8} display="flex" justifyContent="center">
                            <MButton
                                variant="outlined"
                                onClick={onClose}
                            >
                                Cancel
                            </MButton>
                            <MButton
                                sx={{ ml: 2 }}
                                onClick={() => setStep('1.1')}
                                disabled={
                                    !recordingFileData ||
                                    !!(recordingFileData && recordingFileData.fileType !== 'cdm') ||
                                    !!(recordingFileData && recordingFileData.file?.size === 0) ||
                                    !!(logsFileData && logsFileData.fileType !== 'log')
                                }
                            >
                                Confirm
                            </MButton>
                        </Box>
                    </Box>
                );
            default:
                return firstStep;
        }
    }

    return (
        <>
            <MModal
                open={open}
                handleClose={onClose}
                width={600}
                height="auto"
            >
                {modalContent()}
            </MModal>
            <MButton onClick={() => setOpen(true)}>{name}</MButton>
        </>
    );
};

export default UploadDMS;
